import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAssessment } from '@/contexts/AssessmentContext';
import RankedList from '../RankedList';
import { OpportunityScore } from '@/types/opportunity';
import AssessmentActionButtons from './AssessmentActionButtons';
import OpportunitySummaryCard from '../opportunities/OpportunitySummaryCard';
import { Alert, AlertDescription } from '@/components/shared/alert';
import { normalizeScores } from '@/utils/normalization';
import { CheckCircle2 } from 'lucide-react';
import AboutTheScoreSidebar from '../opportunities/AboutTheScoreSidebar';

interface AssessmentResultsStepProps {
  onBack: () => void;
  scoredOpportunities: OpportunityScore[];
}

const AssessmentResultsStep = ({ onBack, scoredOpportunities }: AssessmentResultsStepProps) => {
  const { weights, isShowingSavedAssessment } = useAssessment();
  const [saveState, setSaveState] = useState<'unsaved' | 'saved-showing' | 'saved-hidden'>('saved-hidden');
  const [hasShownUnsavedAlert, setHasShownUnsavedAlert] = useState(false);
  
  // Shared state for the AboutTheScore sidebar to prevent closing on context updates
  const [isAboutScoreSidebarOpen, setIsAboutScoreSidebarOpen] = useState(false);
  
  // Memoized handlers to prevent unnecessary rerenders
  const handleOpenAboutScore = useCallback(() => {
    setIsAboutScoreSidebarOpen(true);
  }, []);

  const handleCloseAboutScore = useCallback(() => {
    setIsAboutScoreSidebarOpen(false);
  }, []);

  // Listen for save events via window events
  useEffect(() => {
    let timeoutId: number | undefined;

    const handleSaveEvent = () => {
      // Only show the saved confirmation if the user has previously seen the unsaved alert
      if (hasShownUnsavedAlert) {
        setSaveState('saved-showing');
        
        // Auto-hide the success message after 5 seconds
        timeoutId = window.setTimeout(() => {
          setSaveState('saved-hidden');
          setHasShownUnsavedAlert(false); // Reset for next cycle
        }, 5000);
      } else {
        // If they haven't seen the unsaved alert, just hide any alerts
        setSaveState('saved-hidden');
      }
    };
    
    window.addEventListener('assessment:saved', handleSaveEvent);
    
    return () => {
      window.removeEventListener('assessment:saved', handleSaveEvent);
      // Clean up the timeout when component unmounts
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasShownUnsavedAlert]);

  // Update save state based on whether we're showing a saved assessment
  useEffect(() => {
    if (isShowingSavedAssessment) {
      setSaveState('saved-hidden'); // No popup for saved assessments
      setHasShownUnsavedAlert(false); // Reset tracking
    } else {
      setSaveState('unsaved'); // Show unsaved popup for new changes
      setHasShownUnsavedAlert(true); // Mark that user has seen the unsaved alert
    }
  }, [isShowingSavedAssessment]);
  
  // Normalize scores consistently for all components
  const normalizedOpportunities = useMemo(() => normalizeScores(scoredOpportunities), [scoredOpportunities]);
  
  // Sort by final score to get the top three
  const sortedOpportunities = useMemo(() => {
    return [...normalizedOpportunities].sort((a, b) => {
      return b.finalScore - a.finalScore; // Descending order
    });
  }, [normalizedOpportunities]);
  
  const topThreeOpportunities = sortedOpportunities.slice(0, 3);

  // Memoize the AboutTheScoreSidebar to prevent rerenders
  const memoizedAboutTheScoreSidebar = useMemo(() => (
    <AboutTheScoreSidebar
      isOpen={isAboutScoreSidebarOpen}
      onClose={handleCloseAboutScore}
    />
  ), [isAboutScoreSidebarOpen, handleCloseAboutScore]);

  return (
    <div className="space-y-12">
      <div className="mb-6">
        <AssessmentActionButtons 
          weights={weights}
          scoredOpportunities={scoredOpportunities}
          onBack={onBack}
        />
        {saveState === 'saved-showing' ? (
          <Alert className="mt-4 bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600 mr-2 mt-1" />
            <AlertDescription className="text-md text-center mt-1">
              Your selections and opportunities have been saved successfully.
            </AlertDescription>
          </Alert>
        ) : saveState === 'unsaved' ? (
          <Alert className="mt-4 bg-blue-50 border-blue-200">
            <AlertDescription className="text-md text-center">
              These results reflect your current selections. Your changes are temporary until you click "Save".
            </AlertDescription>
          </Alert>
        ) : null}
      </div>

      <div className="container mx-auto px-4 w-full max-w-[1600px]">
        <div className="text-center space-y-4">
          <div className="inline-block px-5 py-2 text-lg font-medium text-white bg-gradient-to-r from-[#CA0079] to-[#F1AB11] rounded-full shadow-lg font-geist-mono mb-4">
            OPPORTUNITY ASSESSMENT
          </div>
        <h2 className="text-4xl md:text-5xl text-gray-800 pb-4 font-medium">Your Top 3 Opportunities</h2>
        <p className="text-xl md:text-2xl text-gray-600 max-w-5xl mx-auto font-light pb-8">
          Based on your answers, we believe these 3 solutions can significantly streamline and strengthen your operations.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          {topThreeOpportunities.map((opportunity, index) => (
            <OpportunitySummaryCard 
              key={`${opportunity.Name}-rank-${index + 1}-score-${opportunity.finalScore.toFixed(3)}`}
              opportunity={opportunity}
              rank={index + 1}
              onOpenAboutScore={handleOpenAboutScore}
            />
          ))}
        </div>
      </div>
      </div>
      
      <RankedList scoredOpportunities={normalizedOpportunities} onOpenAboutScore={handleOpenAboutScore} />
      
      {/* Shared AboutTheScore sidebar - isolated from opportunity card rerenders */}
      {memoizedAboutTheScoreSidebar}
    </div>
  );
};

export default AssessmentResultsStep;